import { t, Trans } from '@lingui/macro';
import {
  Button2,
  convertCentsIntegerToClosestFiveDollarString,
  CrossIcon,
  ImageKitImage,
  Link2,
  ReactFC,
  SideScroller,
  Text,
} from '@milo/react';
import { verticalsIdsColorMap, verticalsIdsNameMap } from '../components/Search/VerticalsFilter';

export const RabaisActivites: ReactFC = () => {
  return (
    <div className="web-container">
      {/* Image header */}
      <ImageKitImage
        src={'/images/rabais-activites/header.png'}
        alt="Montreal Activities"
        className="h-full w-full rounded-xl"
      />

      {/* Title + Call to action */}
      <div className="my-12 flex flex-col items-center">
        <h1 className="text-4xl font-bold">
          <Trans>Obtenez les meilleurs rabais sur des activités locales</Trans>
        </h1>
        <h1 className="text-4xl">
          <Trans>Des bonnes adresses 100% Québec</Trans>
        </h1>
        <div className="mt-8 flex flex-row justify-center md:flex-col">
          <Link2 href={`/purchase`}>
            <Button2 className=" !min-w-[300px] text-2xl !font-bold">
              <Trans>Je découvre le pass M ta région</Trans>
            </Button2>
          </Link2>
        </div>
      </div>
      <div className="mb-16">
        <MiloLandingDescription>
          <Trans>
            À la recherche d'aventures et de découvertes? M ta région vous accompagne dans des centaines d'expériences
            100% québécoises. Que vous soyez entre amis, en famille ou ou en couple...
            <br />
            Profitez de rabais sur de nombreuses activités comme spa, golf, jeux d'évasion...
          </Trans>
        </MiloLandingDescription>
      </div>

      <LandingLogos />

      {/* 3 reasons */}
      <div className="mb-16 flex flex-col items-center gap-16 md:flex-row">
        <img
          className="hidden rounded-3xl object-cover object-center lg:flex lg:w-1/2"
          src="/images/rabais-activites/placeholder.png"
          alt="Plusieurs rabais sur des activités 100% Québécoises"
        />
        <div className="mx-auto flex max-w-2xl flex-col">
          <div className="flex text-4xl font-bold text-raspberry">
            <Trans>3 raisons d'utiliser le pass M ta région</Trans>
          </div>
          <ThreeReasons />
        </div>
      </div>

      {/* Experiences */}
      <ExperiencesScroller />

      {/* Call to action */}
      <div className="mb-16 flex flex-row justify-center">
        <Link2 href={`/experiences`}>
          <Button2 className="text-xl !font-bold md:!text-xl">
            <Trans>Voir toutes les expériences</Trans>
          </Button2>
        </Link2>
      </div>

      <Testimonials />
    </div>
  );
};

export const ThreeReasons: ReactFC = () => {
  return (
    <div className="text-center text-xl md:text-justify">
      <div className="mb-6">
        <div className="mb-2 text-2xl font-bold">
          <Trans>Découvrir en économisant</Trans>
        </div>
        <div>
          <Trans>
            Nous proposons des réductions sur nos activités locales allant de 15% à 50% tout en vous faisant découvrir
            le Québec authentique Du nord au sud, vous pourrez élargir vos horizons en utilisant le pass M ta région.
          </Trans>
        </div>
      </div>
      <div className="mb-6">
        <div className="mb-2 text-2xl font-bold">
          <Trans>Gagner du temps</Trans>
        </div>
        <div>
          <Trans>
            Plus besoin de passer des heures à chercher les meilleures adresses où sortir au Québec. L'équipe M ta
            région les a sélectionné avec des critères très précis pour vous permettre de vivre des expériences uniques,
            en toute tranquillité et sans tracas.
          </Trans>
        </div>
      </div>
      <div className="mb-6">
        <div className="mb-2 text-2xl font-bold">
          <Trans>Soutenir les commerçants locaux</Trans>
        </div>
        <div>
          <Trans>
            Il est important pour nous d'encourager la découverte de boutiques artisanales, de restaurants de qualité et
            de plein d'activités originales 100% québécoises. Le but étant de soutenir les petits commerçants, et de
            faire vivre l'économie locale du Québec.
          </Trans>
        </div>
      </div>
    </div>
  );
};

export const MiloLandingDescription: ReactFC = ({ children }) => {
  return (
    <div className="flex flex-col justify-around text-center md:flex-row">
      <h1 className="text-2xl">{children}</h1>
    </div>
  );
};

export const LandingLogos: ReactFC = () => {
  return (
    <div className="mb-16 flex flex-wrap justify-around gap-4 text-center text-3xl font-semibold">
      {/* Picto 1 */}
      <div className="flex flex-none basis-1/5 flex-col items-center gap-4">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-warning">
          <svg xmlns="http://www.w3.org/2000/svg" width="50" fill="white" viewBox="0 0 614 710">
            <path d="M336 1.6c-22.3 4.8-41.7 19.7-51.5 39.7-6.2 12.6-7.9 19.8-7.9 33.2.1 13.3 2 21.9 7.4 33 12.3 25.4 38.1 41.5 66.4 41.5 34.9 0 63.3-21.7 72.7-55.5 1.8-6.2 2.3-10.5 2.2-19-.2-21.3-6.7-36.9-21.8-52.1-8-8.1-10.3-9.8-19.5-14.2-16.2-7.9-32.2-10.1-48-6.6zM271.4 155.5c-6.5 1.4-14 4.4-21.4 8.4-6.1 3.4-19.9 17.4-23.3 23.8-3.8 7.1-61.5 177.3-62.7 185-1.4 8.3.2 17.8 4.2 26.2 3.4 7.2 3.3 7.1 68.8 73.9 20.6 21.1 37.7 38.8 37.9 39.4.2.6-8.6 32.3-19.5 70.4l-19.9 69.3-106.5.3-106.5.3-5.3 2.4C7.4 659.3 0 670.3 0 680.5c0 10.3 6.4 21 15.4 25.8l4.1 2.2h525l8-2.3c23.1-6.6 44.3-22.7 56.5-42.8 3-5.1 3.5-6.8 3.9-13.5.2-6-.1-8.6-1.7-12.1-4.7-10.7-14-16.8-25.4-16.8-11.1 0-17.3 3.5-25.7 14.6-2.7 3.7-7.1 8.2-9.7 10-9 6.4-9.3 6.4-57 6.2l-42.9-.3L478 511.8l27.5-139.7 4.5-2.9c6.1-3.9 11.7-10.3 14.9-17.1 2.3-4.9 2.6-6.9 2.6-15.6 0-8.5-.4-10.7-2.4-14.8-3.4-7-10.1-13.9-17-17.6-5.6-3-8.8-3.6-50.5-9.9-24.5-3.8-44.7-7-44.9-7.2-1-1-47.2-68.8-60.5-88.7-10.3-15.4-15.9-22.7-20.4-26.8-7.6-6.8-21.5-14-31.3-16-8.6-1.8-20.9-1.8-29.1 0zm76.5 170.4c15.1 22.4 20.1 27.8 28.5 30.9 2.9 1.1 23.1 4.5 47.5 8.1 23.4 3.4 42.5 6.2 42.6 6.4.1.1-12.3 63.2-27.4 140.2l-27.6 140-41.3.3c-22.6.1-41.2-.1-41.2-.6 0-.4 10.5-32.9 23.4-72.3 12.9-39.3 23.8-73.7 24.1-76.4.9-6.5-.8-15.5-4.4-23-1.6-3.4-15.5-22.7-32.1-44.5-16.1-21.2-30.3-39.9-31.6-41.5l-2.3-3 12.8-41c13.4-43.4 13.8-44.5 14.4-44.5.3 0 6.8 9.4 14.6 20.9z" />
          </svg>
        </div>
        <Trans>Activités hivernales</Trans>
      </div>

      {/* Picto 2 */}
      <div className="flex flex-none basis-1/5 flex-col items-center gap-4 text-3xl">
        <div className="flex h-24  w-24 items-center justify-center rounded-full bg-teal">
          <svg xmlns="http://www.w3.org/2000/svg" width="55" fill="white" viewBox="0 0 701 655">
            <path d="M567.1 2.3c-1.3.6-3.4 2.5-4.5 4-1.9 2.6-2.1 4.2-2.1 18.2 0 17.6.8 19.8 8.2 22.4 3.6 1.3 4.3 1.2 7.9-.5 6.6-3.2 7.5-6.4 7.2-23.7l-.3-14.9-3.7-3.4c-4.1-3.7-8.3-4.4-12.7-2.1zM90 30.7c-2.6 2.4-3.6 4.6-6 14.3-1.6 6.3-21 85.3-43.1 175.5C.8 384 .7 384.5.7 395c0 8.6.5 12 2.6 18.5 6.7 20.2 23.9 36 44.7 40.9 9.3 2.2 87.1 2.3 97.4.1 21.7-4.6 40.3-22.5 46.2-44.5 1.1-4.2 1.7-9.9 1.6-16-.1-9-2.3-18.6-43.1-185C108 37.2 107.1 33.4 104 30.8c-4.2-3.8-9.8-3.8-14-.1zM333 29.5c-1.4.7-3.1 2.4-3.8 3.7-.7 1.3-20.4 80.6-43.8 176.3-42.4 173.1-42.7 174.1-42.8 185-.1 8.8.3 12.4 2.1 18 6 19.1 21.7 34.7 40.8 40.8 6.8 2.1 8.2 2.2 53.5 2.2h46.5l7.4-2.4c14.9-4.9 29-17 35.6-30.6 5.3-10.7 6.8-18 6.2-30.4-.4-9.8-3.7-24.1-43.1-184.6C350.3 38.6 348.9 33.4 346 30.7c-3.4-3.1-8.7-3.6-13-1.2zM485.4 36c-3.9 1.6-7.4 6.8-7.4 11.2 0 3.9.6 4.7 11.3 15.6 10.5 10.6 11.7 11.4 15.9 11.9 4 .5 4.9.2 8.1-2.7 4.5-3.9 5.6-8.4 3.2-13.3-2-4.3-19.2-21.2-22.9-22.7-3.2-1.2-5.3-1.2-8.2 0zM649.5 36.6c-4.2 2.1-20.2 18.2-22 22.1-2.1 4.4-1.1 9.4 2.7 13 2.4 2.3 3.8 2.8 7.9 2.8h5l11.4-11.6C665.1 52.3 666 51 666 47.6c0-4.9-2.6-9.2-6.6-11.1-4-1.9-6.1-1.9-9.9.1zM556.9 85.4c-32.6 11.5-42.2 52.4-17.9 76.7 27 27.1 72.8 12.1 79.1-25.7 3.6-22.2-10.5-44.7-32.2-51.5-8.7-2.7-20.5-2.5-29 .5zM447.4 121.4c-2.8 2.8-3.4 4.1-3.4 7.8 0 2.4.7 5.4 1.6 6.6 3.1 4.4 6.3 5.2 21.4 5.2 15.1 0 18.3-.8 21.4-5.2.9-1.2 1.6-4.2 1.6-6.6 0-3.7-.6-5-3.4-7.8l-3.4-3.4h-32.4l-3.4 3.4zM657.4 121.4c-2.8 2.8-3.4 4.1-3.4 7.8 0 2.4.7 5.4 1.6 6.6 3.1 4.4 6.3 5.2 21.4 5.2 15.1 0 18.3-.8 21.4-5.2.9-1.2 1.6-4.2 1.6-6.6 0-3.7-.6-5-3.4-7.8l-3.4-3.4h-32.4l-3.4 3.4zM500.5 185.2c-2.9 1.5-9.8 8.3-16.6 16.1-5 5.8-5.9 7.3-5.9 10.6 0 7.3 7.4 12.9 14.5 10.9 4.4-1.2 24.3-21.4 25.1-25.4.8-4.2-2-10-5.7-11.9-3.3-1.8-8.6-1.9-11.4-.3zM631.9 185.5c-3.6 2-6.2 7.6-5.4 11.8.8 4.3 20.4 24.3 25 25.5 4.3 1.2 8.9-.3 11.9-3.9 3.6-4.2 3.5-9.4-.3-14.5-3.2-4.4-15.3-16.3-19.1-18.8-3-2-8.6-2-12.1-.1zM567.1 212.2c-1.3.7-3.4 2.6-4.5 4.1-1.9 2.6-2.1 4.2-2.1 18.2 0 17.6.8 19.8 8.2 22.4 3.6 1.3 4.3 1.2 7.9-.5 6.6-3.2 7.5-6.4 7.2-23.6l-.3-14.8-3.1-3.1c-3.6-3.5-9.5-4.7-13.3-2.7zM70.7 479.7c-.4.3-.7 18.8-.7 41V561h47v-82H94.2c-12.6 0-23.2.3-23.5.7zM315.7 479.7c-.4.3-.7 18.8-.7 41V561h47v-82h-22.8c-12.6 0-23.2.3-23.5.7zM28 585.4c-12.7 2.9-19.5 7.7-24.2 17.4l-3.3 6.7-.3 22.2-.4 22.3h677.4l-.4-22.7c-.3-22.6-.3-22.8-3.1-28.5-4.5-9.1-11.9-14.4-23.9-17.3-5.5-1.3-45.3-1.5-311.3-1.4-238.6.1-306.2.3-310.5 1.3z" />
          </svg>
        </div>
        <Trans>Escapade culturelle</Trans>
      </div>

      {/* Picto 3 */}
      <div className="mb-16 flex flex-none basis-1/5 flex-col items-center gap-4 text-3xl">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-green">
          <svg xmlns="http://www.w3.org/2000/svg" width="55" fill="white" viewBox="0 0 770 747">
            <path d="M299.1 1c-23.1 4.9-41.2 19-50.6 39.5-13.7 29.5-4.8 64.8 21 84.3 34.7 26.1 84.4 14.4 103.9-24.4 5.2-10.5 7-18.2 7-30.9 0-20.3-5.6-33.7-19.9-48.1-9.9-10-16-13.9-28-17.9-8.9-3-25.2-4.2-33.4-2.5zM415.6 58.6c-1.4 1.3-1.6 4.9-1.6 24v22.5l2.3-.8c1.2-.4 6.3-.8 11.2-.8 10.4.1 14.4 1.7 21.2 8.4 8.4 8.5 11.4 21.1 7.6 32.3-1.9 5.5-1.9 5.8-.2 5.8 3.8 0 4.9-3.7 4.9-16.7V121h13v4.3c0 8-1.4 7.7 41.3 7.7H553V74h-37.8c-42.9 0-41.2-.3-41.2 7.8V86h-13V73.7c0-17.8 1.6-16.7-23.5-16.7-17.2 0-20.6.2-21.9 1.6zM453 68v3h-32v-6h32v3zM105.3 61c-16.2 4.2-33.7 14.7-48.9 29.2-15.3 14.7-22.8 28.3-24.1 43.7-.7 7.9 1.4 18.8 5.1 26.6 5.5 11.8 17 23.7 24.4 25.1 1.7.3 3.2 1 3.2 1.4 0 .4-4.8 4.1-10.7 8.1-12.2 8.4-27 22.1-34.4 31.8-6.5 8.6-14.2 24.4-17.1 35.1-1.9 7.1-2.3 11-2.2 23.5 0 12.8.4 16.5 2.7 25.7 8.9 35.9 31 71.3 56.2 90.1 27.5 20.5 62.7 26.3 87.2 14.2 9.7-4.8 13.2-9.1 17.4-21.5 1.9-5.5 3.6-10 3.9-10 .3 0 2 3.2 3.8 7 1.7 3.8 5.6 9.5 8.5 12.7l5.3 5.8-25.8 72c-14.2 39.6-37.6 104.8-52 145l-26.3 73v9c0 8.2.3 9.7 3.3 15.7 5.9 12 15.8 19.5 28.6 21.9 14.3 2.6 30.6-4.5 38.3-16.7 1.9-3.1 22.1-58.1 54.3-147.9 28.1-78.6 51.3-143.1 51.4-143.2.1-.2 24 2 53.1 4.7 29.1 2.8 53.7 5 54.6 5 1.2 0 11.2 17.7 35.4 62.7 18.6 34.6 35.1 64.7 36.8 67.1 4.1 5.7 13 11.5 21.1 13.7 17.3 4.7 35.7-3.8 43.8-20.3 4.3-8.6 5.1-20.5 2.1-29.2-1.1-3-19.5-38.1-40.9-78-36.6-68.1-39.4-72.9-45.5-79.1-10.3-10.6-8.1-10.1-80.9-17-34.4-3.2-62.8-5.9-63.2-5.9-1.1 0-.9-28.6.2-42.5 1.4-15.9 4.3-35.8 8.6-58.9 2.2-11.9 3.1-19.2 2.5-19.5-.5-.4-11.2-2.6-23.8-5-24.6-4.7-29.6-6.4-35.6-12.4-8.7-8.7-11.6-23.4-6.8-34.3 4.4-10 15-17.8 25.9-19.1 3.9-.4 28.4 3.5 44.5 7.1 1.6.4 1.8 0 1.2-2.2-2.7-11.5-12.1-23.4-22.3-28.4-6.3-3.2-7.2-3.3-18.2-3.3-6.3 0-13.3.4-15.5.8l-4 .8-2.9-5.3c-13.2-24-33.6-48.3-52.1-62.1-9.3-7-25.2-14.8-34.6-17.1-9.1-2.2-26.3-2-35.6.4zm25.9 32.1c3.4 1.1 8 3.3 10.2 4.8 5.2 3.6 12.9 11.8 15.1 16.1 1.6 3.1 1.6 3.3-.3 5.2-1.9 2-2 2-6.6-2.7-2.7-2.6-7.6-6-11.4-7.8-5.7-2.7-7.7-3.1-14.7-3.1-5.9 0-9.5.6-13.8 2.3-7.4 2.9-16.7 11.4-20.2 18.5-7.1 14.5-4.2 31.7 7.2 43.1 8.4 8.4.7 7.6-9.5-1-4.4-3.7-9.7-12.2-12.2-19.6-1.8-5.3-2.1-8.1-1.8-15.9.3-8.1.8-10.5 3.5-16.2 5.4-11.4 14.8-19.8 26.8-24 7.2-2.5 19.9-2.4 27.7.3zm4.2 30.1c5.5 1.6 14.8 10.9 16.4 16.4 4.2 14.3-3.5 28.9-17.3 32.9-14.7 4.3-29.3-4.7-32.5-20-3.9-18.9 14.3-34.9 33.4-29.3zM559 103.5V133h12.9c12.3 0 13.1-.1 14.5-2.2 1.4-1.9 1.6-6.3 1.6-27.9 0-23.5-.1-25.7-1.8-27.2-1.5-1.4-4.1-1.7-14.5-1.7H559v29.5z" />
            <path d="M421.4 109.9c-8.8 2.1-10.3 3.8-41.1 45.6-16.3 22-29.7 40.1-29.8 40.3-.1.1-22.4-4.1-49.6-9.3-27.2-5.2-51.4-9.5-53.9-9.5-6 0-13.9 3.8-17.9 8.6-10.8 13.1-5.7 33 10.1 38.8 2.4.8 30.6 6.6 62.8 12.7 54.2 10.4 58.8 11.1 63.6 10.1 2.9-.6 6.8-2.1 8.8-3.4 2.5-1.6 14.6-17.1 38.3-49.1 19-25.8 35.4-48.6 36.4-50.7 2.6-5.4 2.5-15.1-.2-20.7s-9.1-11-15.3-12.8c-5.3-1.6-7.4-1.7-12.2-.6zM747.4 205.9c-6.1 1.5-13.8 6.7-19.9 13.4l-6.3 6.9-7.3-2.8c-5.8-2.1-9.3-2.7-15.9-2.8-12.3-.1-17.5 2.9-21.5 12.7l-1.6 3.8 4.9-2.5c9.9-5 22-4.3 37 2.1 4.6 1.9 8.4 3.4 8.6 3.2 14.3-19.3 23.7-26.2 35.7-26.3l5.8-.1-2.1-2.2c-4-4.3-11.9-6.7-17.4-5.4zM632.2 276.1c-4.2 2-8.3 5.3-12.9 10.2l-6.7 7.3-8-2.9c-17-6.2-30.2-3.4-35.7 7.5-2.8 5.5-2.4 5.9 3.4 3.3 9.4-4.4 24.6-3 38.1 3.5 3.4 1.6 6.1 2.8 6.1 2.7.1-.1 1.8-2.5 3.7-5.2 9.3-12.9 22.4-21.5 32.7-21.5h5.4l-1.9-2.1c-2.7-3.1-9.3-5.9-14-5.9-2.6 0-6.3 1.1-10.2 3.1zM729.9 314.4c-6.1 2.2-10.5 5.4-17.1 12.5l-6.4 6.9-8.4-2.9c-15.9-5.4-28.2-3.4-34.2 5.6-1.4 2.2-2.9 5-3.3 6.2-.6 2.2-.6 2.2 4.1-.4 9.1-5.1 24.2-4 39 2.7 3.7 1.7 6.8 2.9 6.9 2.8 2.3-3.9 8.6-11.6 12.8-15.5 6.9-6.6 14.5-10.4 21.9-11.1 5.8-.5 5.8-.5 4.1-2.5-2.4-2.7-9.1-5.7-12.8-5.6-1.6 0-4.6.6-6.6 1.3zM588.3 499.5c-8.8 4.1-11.3 5.8-11.7 7.7-.3 1.2-1.9 11.7-3.6 23.3-2.5 17-3.5 21.3-5 22.6-1.1 1-15.1 10.9-31.2 22.2l-29.3 20.4-64.6 27.7-64.6 27.7-40.9 44c-22.5 24.1-41 44.4-41.2 44.9-.3.7 81.7 1 236.2 1 130.1 0 236.6-.3 236.6-.8 0-.4-11.6-31.8-25.8-69.8l-25.7-69.1-58.3-53.7c-32-29.5-58.6-53.6-59-53.5-.4 0-5.7 2.4-11.9 5.4z" />
          </svg>
        </div>
        <Trans>
          Évasion <br /> plein air
        </Trans>
      </div>

      {/* Picto 4 */}
      <div className="flex flex-none basis-1/5 flex-col items-center gap-4 text-3xl">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-medium-gray">
          <div className="rotate-45">
            <CrossIcon fill="white" width="45" />
          </div>
        </div>
        <Trans>Et bien plus encore</Trans>
      </div>
    </div>
  );
};

const ExperiencesScroller: ReactFC = () => {
  return (
    <>
      <h3 className="mb-5 text-4xl font-semibold">
        <Trans>Une sélection des meilleures activités à faire</Trans>
      </h3>
      <SideScroller className="mb-10">
        <ExperienceItem
          experience={{
            image: '2381/c12439ff-a79a-4163-a022-34bbd253ed4b.jpg',
            slug: 'scandinave-spa-2380',
            name: 'Scandinave Spa',
            description: 'Expérience de bains scandinaves, une activité incontournable du Québec.',
            savings: 10000,
            category: 3,
          }}
        />
        <ExperienceItem
          experience={{
            image: '1789/e67689cc-d2ac-4bbb-9361-de5823823c47.jpg',
            slug: 'chalets-lanaudiere-chalets-1789',
            name: 'Chalets Lanaudière',
            description: "Un séjour inoubliable vous attend dans l'une des yourtes ou cabanes du domaine",
            savings: 10000,
            category: 2,
          }}
        />
        <ExperienceItem
          experience={{
            image: '2111/8ff00d28-b9a2-4588-948e-b71e7ee6f5a0.jpeg',
            slug: 'boutique-josephine-2111',
            name: 'Boutique Joséphine',
            description: 'Cette boutique offre des produits uniques, colorés, de qualité et faits au Québec.',
            savings: 5000,
            category: 4,
          }}
        />
        <ExperienceItem
          experience={{
            image: '4349/a2b0892d-7b35-449c-ba47-85cda2dfd0df.jpg',
            slug: '4-origines-microbrasserie-4349',
            name: '4 Origines - Microbrasserie',
            description: 'La meilleure microbrasserie de Montréal qui propose des bières de qualité.',
            savings: 2500,
            category: 1,
          }}
        />
      </SideScroller>
    </>
  );
};

const ExperienceItem: ReactFC<{
  experience: {
    name: string;
    slug: string;
    image: string;
    description: string;
    savings: number;
    category: number;
  };
}> = ({ experience }) => {
  return (
    <Link2 className="flex animate-fade-in-top flex-col" href={`/experiences/${experience.slug}`}>
      <div className="flex animate-fade-in-top flex-col ">
        <div className="relative mb-2 h-52 w-60 rounded-3xl bg-white">
          <>
            <ImageKitImage imageFileName={experience.image} className="h-full w-full rounded-3xl" />
            <div className="absolute top-0 flex w-full flex-row items-center justify-between rounded-t-3xl bg-gradient-to-b from-medium-dark">
              <Text size="h3" className="p-4 font-bold text-white">
                -{convertCentsIntegerToClosestFiveDollarString(experience?.savings)}
              </Text>
            </div>
            <div
              className={`absolute -bottom-2 z-toast px-2 py-1 text-white bg-${
                verticalsIdsColorMap[experience.category]
              } rounded`}
            >
              {verticalsIdsNameMap[experience.category]}
            </div>
          </>
        </div>

        <Text size="h4" className="my-4 font-semibold">
          {experience.name}
        </Text>

        <Text size="p" className="">
          {experience.description}
        </Text>
      </div>
    </Link2>
  );
};

export const Testimonials: ReactFC = () => {
  return (
    <>
      <h3 className="mb-5 text-4xl font-semibold">
        <Trans>Témoignages</Trans>
      </h3>

      <SideScroller>
        <Testimonial
          text={t`Je cherchais des activités à faire en famille et je suis tombé sur l'offre de M ta région. Cela fait quelques semaines que nous utilisons le pass M ta région et nous sommes ravis. Nous profitons de plein d'activités hivernales à rabais.`}
          author={t`Nathalie`}
          authorImage="/images/rabais-activites/testimonial-1.jpg"
          city={t`Blainville`}
        />
        <Testimonial
          text={t`Avec une amie, nous sommes allés au Strom spa à Québec et c'était vraiment incroyable. En plus, grâce au pass M ta région nous avons obtenu un gros rabais sur cette activité.`}
          author={t`Kevin`}
          authorImage="/images/rabais-activites/testimonial-2.jpg"
          city={t`Rawdon`}
        />
        <Testimonial
          text={t`J'ai offert un pass M ta région à mon cheum ! Nous avons profité du bon plan pour faire un jeu d'évasion. C'était une belle expérience immersive et très originale. Nous avons vraiment adoré cette activité et nous allons en faire encore plein d'autres.`}
          author={t`Juliette`}
          authorImage="/images/rabais-activites/testimonial-3.jpg"
          city={t`Lévis`}
        />
        <Testimonial
          text={t`J'ai acheté un pass M ta région il y a peu ! Je me suis amusée à tester plusieurs activités: jeux d'évasion, spa, traineau à chiens.. c'était vraiment l'fun ! Le gros plus, c'est qu'il y avait des rabais sur chaque activité ce qui m'a permis d'économiser.`}
          author={t`Adeline`}
          authorImage="/images/rabais-activites/testimonial-4.png"
          city={t`Gatineau`}
        />
        <Testimonial
          text={t`J'ai adoré chaque activités que j'ai fais avec mon pass M ta région ! Chaque semaine je l'utilise pour découvrir de nouvelles expériences. Merci M ta région pour les activités de qualité que vous proposez.`}
          author={t`Maxime`}
          authorImage="/images/rabais-activites/testimonial-5.png"
          city={t`Sherbrooke`}
        />
      </SideScroller>
    </>
  );
};

const Testimonial: ReactFC<{ text: string; author: string; city: string; authorImage: string }> = ({
  text,
  author,
  city,
  authorImage,
}) => {
  return (
    <div className="mb-5 flex w-3/5 min-w-[400px] flex-none items-center gap-4 rounded-3xl border border-gray-lighter p-4 shadow-lg">
      <div className="flex flex-none basis-1/5 flex-col items-center">
        <img className="mb-2 !w-32" src={authorImage} alt="M ta région témoignage" />
        <div className="text-center text-lg">
          <p className=" text-center font-bold leading-tight">{author}</p>
          <p>{city}</p>
        </div>
      </div>
      <p className="text-lg">{text}</p>
    </div>
  );
};
